import { action, ActionType } from 'typesafe-actions';

import { IMapSettings }                            from 'views/components/Video/MapComponent';
import { ISettings }                               from './settings';
import { UserColumns }                             from '../types/system';
import { routes }                                  from '../routes';
import { checkCrudPermissionsDirectly, CrudIndex } from '../utils/permissions';
import handbooks                                   from '../views/pages/Handbooks/SpreadSheetHandbook/configs';

export enum actionTypes {
  PERFORM_LOGIN                  = 'PERFORM_LOGIN',
  PERFORM_LOGIN_AD               = 'PERFORM_LOGIN_AD',
  PERFORM_LOGIN_FAILURE          = 'PERFORM_LOGIN_FAILURE',
  PERFORM_LOGIN_FINISH           = 'PERFORM_LOGIN_FINISH',

  LOG_OUT                        = 'LOG_OUT',
  LOG_OUT_SUCCESS                = 'LOG_OUT_SUCCESS',
  SET_LOG_OUT_PROCESS            = 'SET_LOG_OUT_PROCESS',

  SET_USER                       = 'SET_USER',

  SET_COLUMNS_VISIBILITY         = 'SET_COLUMNS_VISIBILITY',
  SET_COLUMNS_VISIBILITY_SUCCESS = 'SET_COLUMNS_VISIBILITY_SUCCESS',
  SET_COLUMNS_VISIBILITY_FAILURE = 'SET_COLUMNS_VISIBILITY_FAILURE',

  GET_COLUMNS_VISIBILITY = 'GET_COLUMNS_VISIBILITY',
  GET_COLUMNS_VISIBILITY_SUCCESS = 'GET_COLUMNS_VISIBILITY_SUCCESS',
  GET_COLUMNS_VISIBILITY_FAILURE = 'GET_COLUMNS_VISIBILITY_FAILURE',

  SET_SEVERAL_COLUMNS = 'SET_SEVERAL_COLUMNS',
  SET_SEVERAL_COLUMNS_SUCCESS = 'SET_SEVERAL_COLUMNS_SUCCESS',
}

export interface ITransportType {
  code: string;
  title: string;
  color: string;
}

export type Permissions = Record<string, number>;
export type RoleSettings = Record<string, any>;

export interface IUser {
  contacts: string;
  email: string;
  firstName: string;
  hasAnyBranchAccess: boolean;
  id: number;
  isBlocked: boolean;
  lastName: string;
  middleName: string;
  permissions: Permissions;
  roleSettings: RoleSettings;
  title: string;
  username: string;
  mapSettings: IMapSettings;
  settings: ISettings;
  transporttype: ITransportType[];
  cameratype: ICameraType[];
  vehicle_count: number;
  users_count: number;
  build: string;
  table_visibility: { table_name: string; fields: string[] }[];
  sessionId: string;
}

export const actions = {
  performLogin: (payload: any) => action(actionTypes.PERFORM_LOGIN, payload),
  performLoginViaAd: (payload: any) => action(actionTypes.PERFORM_LOGIN_AD, payload),
  performLoginSuccess: (payload: IUser) =>
    action(actionTypes.SET_USER, payload),
  performLoginFailure: (payload: any) =>
    action(actionTypes.PERFORM_LOGIN_FAILURE, payload),
  performLoginFinish: () => action(actionTypes.PERFORM_LOGIN_FINISH),
  logOut: () => action(actionTypes.LOG_OUT),
  logOutSuccess: () => action(actionTypes.LOG_OUT_SUCCESS),
  setLogOutProcess: (isLogoutLoading: boolean) => action(actionTypes.SET_LOG_OUT_PROCESS, { isLogoutLoading }),

  setUser: (payload: IUser) => action(actionTypes.SET_USER, payload),

  getColumns: (payload: { table: string }) =>
    action(actionTypes.GET_COLUMNS_VISIBILITY, payload),
  getColumnsSuccess: (payload: UserColumns) =>
    action(actionTypes.GET_COLUMNS_VISIBILITY_SUCCESS, payload),

  setColumns: (payload: { table: string; value: boolean; column: string }) =>
    action(actionTypes.SET_COLUMNS_VISIBILITY, payload),

  setSeveralColumns: (payload: { table: string; columns: string[] }) =>
    action(actionTypes.SET_SEVERAL_COLUMNS, payload),
};

export interface ICameraType {
  ip: string,
  title: string
}

export interface IUserState {
  userFirstName?: string;
  userLastName?: string;
  userEmail?: string;
  userId?: number;
  title?: string;
  error?: object;
  username?: string;
  isLogged: boolean;
  isProcessing: boolean;
  permissions: Permissions;
  roleSettings: RoleSettings;
  availablePath: string;
  vehicle_count: number;
  users_count: number;
  build: string;
  transporttypes: ITransportType[];
  cameratype: ICameraType[];
  columns: { [key: string]: string[] };
  loaded: boolean;
  isLogoutLoading: boolean;
  sessionId: string;
}

const initialState: IUserState = {
  isLogged: !!localStorage.getItem('token'),
  isProcessing: false,
  permissions: {},
  roleSettings: {},
  availablePath: '',
  vehicle_count: 0,
  users_count: 0,
  build: '',
  transporttypes: [],
  cameratype: [],
  columns: {},
  loaded: false,
  isLogoutLoading: false,
  sessionId: '',
};

export type ActionTypes = ActionType<typeof actions>;

const reducer = (
  state: IUserState = initialState,
  action: ActionTypes
): IUserState => {
  switch (action.type) {
    case actionTypes.PERFORM_LOGIN:
      return {
        ...state,
        isProcessing: true,
      };
    case actionTypes.PERFORM_LOGIN_FINISH:
      return {
        ...state,
        isProcessing: false,
      };
    case actionTypes.PERFORM_LOGIN_FAILURE:
      return {
        ...state,
        error: action.payload.error,
        isProcessing: false,
      };
    case actionTypes.PERFORM_LOGIN_AD:
      return {
        ...state,
        isProcessing: true,
      };
    case actionTypes.SET_USER:
      const transporttypes: ITransportType[] = [];
      if (action.payload.transporttype) {
        action.payload.transporttype.forEach((transport: ITransportType) => {
          if (
            !!transport.code &&
            transport.code !== '-' &&
            transport.title !== '-'
          )
            transporttypes.push(transport);
        });
      }


      let availablePath = routes.find((route) =>
                                        route.alwaysAvailable ||
                                        checkCrudPermissionsDirectly(
                                          CrudIndex.READ,
                                          route.path,
                                          action.payload.permissions ?? {},
                                          action.payload.id,
                                        ))?.path;

      if (availablePath && availablePath === '/handbooks/table/:handbookType') {
        const handbooksKeys = Object.keys(handbooks);

        const availableHandbooks = handbooksKeys.find((key) =>
                                             checkCrudPermissionsDirectly(
                                               CrudIndex.READ,
                                               `/handbooks/table/${key}`,
                                               action.payload.permissions ?? {},
                                               action.payload.id,
                                             ));

        if (availableHandbooks) {
          availablePath = `/handbooks/table/${availableHandbooks}`;
        }
      }

      return {
        ...state,
        isLogged: true,
        isProcessing: false,
        userFirstName: action.payload.firstName,
        userLastName: action.payload.lastName,
        permissions: action.payload.permissions ?? {},
        roleSettings: action.payload.roleSettings ?? {},
        availablePath: availablePath ?? '/not-found',
        title: action.payload.title,
        username: action.payload.username,
        userId: action.payload.id,
        sessionId: action.payload.sessionId,
        vehicle_count: action.payload.vehicle_count,
        users_count: action.payload.users_count,
        build: action.payload.build,
        transporttypes: transporttypes,
        cameratype: action.payload.cameratype,
        columns:
          action.payload.table_visibility &&
          action.payload.table_visibility.reduce(
            (res, cur) => ({
              ...res,
              [cur.table_name]: cur.fields,
            }),
            {}
          ),
        loaded: true,
      };
    case actionTypes.LOG_OUT_SUCCESS:
      return {
        ...state,
        isLogged: false,
        isLogoutLoading: false,
      };
    case actionTypes.SET_LOG_OUT_PROCESS:
      return {
        ...state,
        isLogoutLoading: action.payload.isLogoutLoading,
      };
    case actionTypes.GET_COLUMNS_VISIBILITY_SUCCESS:
      return {
        ...state,
        columns: {
          ...state.columns,
          [action.payload.table]: action.payload.columns,
        },
      };
    default:
      return state;
  }
};

export default reducer;
